import { useState } from 'react';
import { motion } from 'framer-motion';
import { Leaf, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useTastingStore } from '@/stores/tastingStore';
import { COMMON_BOTANICALS } from '@/types';

export default function BotanicalsStep() {
  const {
    getCurrentSpirit,
    currentSpiritIndex,
    updateSpirit,
    previousStep,
    nextStep,
  } = useTastingStore();

  const spirit = getCurrentSpirit();
  const [selected, setSelected] = useState<string[]>(spirit?.detected_botanicals || []);

  const toggleBotanical = (botanical: string) => {
    setSelected((prev) =>
      prev.includes(botanical)
        ? prev.filter((b) => b !== botanical)
        : [...prev, botanical]
    );
  };

  const handleSave = () => {
    updateSpirit(currentSpiritIndex, {
      detected_botanicals: selected.length > 0 ? selected : undefined,
    });
    nextStep();
  };

  const handleSkip = () => {
    nextStep();
  };

  if (!spirit) {
    return (
      <div className="text-center py-12">
        <p className="text-dark-400">Keine Spirituose ausgewählt</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="badge-primary mb-4">
          Spirituose {currentSpiritIndex + 1}
        </div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-dark-50 mb-2">
          {spirit.spirit_name}
        </h1>
        {spirit.spirit_brand && (
          <p className="text-dark-400">{spirit.spirit_brand}</p>
        )}
      </div>

      {/* Step Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Leaf className="w-8 h-8 text-primary-500" />
        </div>
        <h2 className="text-xl font-semibold text-dark-100 mb-2">Botanicals</h2>
        <p className="text-dark-400 text-sm max-w-md mx-auto">
          Welche Botanicals (Zutaten) erkennst du?
          Dies ist optional und macht mehr Spaß als Rätsel.
        </p>
      </motion.div>

      {/* Selected count */}
      {selected.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <span className="badge-primary">
            {selected.length} ausgewählt
          </span>
        </motion.div>
      )}

      {/* Botanicals Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <div className="flex flex-wrap gap-2">
          {COMMON_BOTANICALS.map((botanical) => {
            const isSelected = selected.includes(botanical);
            return (
              <button
                key={botanical}
                onClick={() => toggleBotanical(botanical)}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium
                  transition-all duration-200
                  ${isSelected
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500'
                    : 'bg-dark-800 text-dark-300 border border-dark-700 hover:border-primary-500/50 hover:text-primary-400'
                  }
                `}
              >
                {isSelected && <Check className="w-4 h-4 inline mr-1" />}
                {botanical}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Selected Summary */}
      {selected.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-primary-500/10 border-primary-500/30"
        >
          <h3 className="text-sm font-semibold text-dark-100 mb-2">Deine Auswahl:</h3>
          <p className="text-dark-300 text-sm">{selected.join(', ')}</p>
        </motion.div>
      )}

      {/* Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex gap-3"
      >
        <button
          onClick={previousStep}
          className="btn-secondary flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
        {selected.length === 0 ? (
          <button
            onClick={handleSkip}
            className="btn-secondary flex-1"
          >
            Überspringen
          </button>
        ) : (
          <button
            onClick={handleSave}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            Weiter
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </motion.div>
    </div>
  );
}
